<template>
  <div>
    <Back @click="handleBack" />

    <DownloadButton class="mb-24" @click="handleDownloadCSV" />

    <ActionRequired
      v-if="!isListingType"
      :category-id="selectedCategory!.marketplaceCategoryId"
      :category-name="selectedCategory!.categoryName"
      class="mb-24"
    />

    <h2 class="heading-2 mb-32">
      {{ i18n(translations.completeCsv) }}
    </h2>

    <div v-if="isListingType" class="mb-7">
      <h3 class="heading-3">{{ i18n(translations.matchingKeys) }}</h3>
      <div class="mb-20">{{ i18n(translations.matchinKeysInfo) }}</div>
      <RevTable
        :caption="i18n(translations.matchingKeys)"
        :collection="FIELDS.SPECIFIC_LISTING"
        :columns
        transparent-header
      >
        <template #body-required="{ item }">
          <RevTag
            v-if="item.required"
            :label="i18n(translations.mandatory)"
            variant="primary"
          />
          <RevTag
            v-if="!item.required"
            :label="i18n(translations.optional)"
            variant="secondary"
          />
          <div v-if="item.shouldBeUniq" class="text-caption">
            {{ i18n(translations.shouldBeUniq) }}
          </div>
        </template>
        <template #body-example="{ item }">
          {{ getExampleLabel(item) }}
        </template>
      </RevTable>
    </div>

    <RevTable
      :caption="i18n(translations.listingAttributes)"
      class="mb-32 overflow-x-scroll"
      :collection="FIELDS.COMMON_LISTING"
      :columns="columnsWithAllowedValues"
      transparent-header
    >
      <template #body-required="{ item }">
        <RevTag
          v-if="item.required"
          :label="i18n(translations.mandatory)"
          variant="primary"
        />
        <RevTag
          v-if="!item.required"
          :label="i18n(translations.optional)"
          variant="secondary"
        />
      </template>
      <template #body-example="{ item }">
        {{ getExampleLabel(item) }}
      </template>
      <template #body-allowedValues="{ item }">
        {{ formatAllowedValues(item) }}
      </template>
    </RevTable>

    <RevTable
      v-if="!isListingType"
      :caption="i18n(translations.productAttributes)"
      class="mb-32"
      :collection="productKeys"
      :columns="columnsWithAllowedValues"
      transparent-header
    >
      <template #body-required="{ item }">
        <RevTag
          v-if="item.required"
          :label="i18n(translations.mandatory)"
          variant="primary"
        />
        <RevTag
          v-if="!item.required"
          :label="i18n(translations.optional)"
          variant="secondary"
        />
      </template>
      <template #body-example="{ item }">
        {{ getExampleLabel(item) }}
      </template>
      <template #body-allowedValues="{ item }">
        {{ formatAllowedValues(item) }}
      </template>
    </RevTable>

    <Tips />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevTable } from '@ds/components/Table'
import { RevTag } from '@ds/components/Tag'

import { generateAndDownloadCSV } from '~/utils/generateAndDownloadCSV'

import {
  type TransformedCategory,
  UploadTypes,
} from '../../BatchListingCreation.types'

import { FIELDS } from './Summary.constants'
import translations from './Summary.translations'
import { sortByRequired } from './Summary.utils'
import ActionRequired from './components/ActionRequired/ActionRequired.vue'
import Back from './components/Back/Back.vue'
import DownloadButton from './components/DownloadButton/DownloadButton.vue'
import Tips from './components/Tips/Tips.vue'

const props = defineProps<{
  uploadType: UploadTypes
  selectedCategory?: TransformedCategory | null
}>()

const emit = defineEmits(['back'])

const i18n = useI18n()

const columns = [
  { key: 'required', label: '' },
  { key: 'field', label: i18n(translations.columnName) },
  { key: 'type', label: i18n(translations.format) },
  { key: 'example', label: i18n(translations.example) },
]

const columnsWithAllowedValues = [
  ...columns,
  { key: 'allowedValues', label: i18n(translations.allowedValues) },
]

const isListingType = computed(
  () => props.uploadType === UploadTypes.listingsOnly,
)

const productKeys = computed(() => {
  const productFields = [...FIELDS.SPECIFIC_PRODUCT, ...FIELDS.COMMON_PRODUCT]
    .sort(sortByRequired)
    .map((productField) =>
      productField.field !== 'category_id'
        ? productField
        : {
            ...productField,
            allowedValues:
              props.selectedCategory?.marketplaceCategoryId.toString(),
          },
    )

  return [...productFields, ...(props.selectedCategory?.attributes || [])]
})

const csvFields = computed(() => [
  ...FIELDS.COMMON_LISTING,
  ...(isListingType.value ? FIELDS.SPECIFIC_LISTING : productKeys.value),
])

function getExampleLabel(
  item:
    | {
        translationKey?: string
      }
    | {
        example?: string
      }
    | object,
) {
  if (
    'translationKey' in item &&
    translations[item.translationKey as keyof typeof translations]
  ) {
    return i18n(translations[item.translationKey as keyof typeof translations])
  }

  return '-'
}

type Product = (typeof productKeys.value)[number]

const formatAllowedValues = (item: Product) => {
  if ('allowedValues' in item && item.allowedValues) {
    return item.allowedValues.replaceAll(',', ', ')
  }

  return '-'
}

function handleBack() {
  emit('back')
}

function handleDownloadCSV() {
  const rows = [
    [...csvFields.value.map((field) => field.field)],
    [
      ...csvFields.value.map((field) =>
        field.required ? 'Required' : 'Optional',
      ),
    ],
    [...csvFields.value.map((field) => field.type)],
    [
      ...csvFields.value.map((field) => {
        if (formatAllowedValues(field) === '-') {
          return getExampleLabel(field)
        }

        return formatAllowedValues(field)
      }),
    ],
  ]

  generateAndDownloadCSV({ rows, filename: props.uploadType })
}
</script>
